import React, { useState } from "react";
import { Box, Button, Switch, FormControlLabel, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { createFormFiller } from "../../../actions/form-filler/createFormFiller";
import Dropzone from "../../../components/forms/GenericDropzone";
import UploadedFilesList from "../../../components/lists/UploadedFilesList";
import PdfPasswordModal from "../../../components/modals/PdfPasswordModal";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import { useDualUploadForm } from "../../../hooks/useDualUploadForm";
import FormFillerList from "../../form-filler/FormFillerList";
import GlassPanel from "../GlassPanel";

const FormFillerTab: React.FC<{ isApproved: boolean }> = ({ isApproved }) => {
  const uploadForm = useDualUploadForm({
    primaryMinFiles: 1,
    secondaryMinFiles: 0,
    primaryErrorMessage: "Upload one PDF or DOCX form template.",
  });

  const [answersText, setAnswersText] = useState("");
  const [intelligentMode, setIntelligentMode] = useState(false);

  const hasForm = uploadForm.primaryFiles.length === 1;
  const hasAnswers = uploadForm.secondaryFiles.length > 0 || !!answersText.trim();
  const canRun = hasForm && hasAnswers;

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (primaryKeys, secondaryKeys, filePasswords) =>
      createFormFiller(primaryKeys[0] || "", secondaryKeys, {
        answersText,
        intelligentMode,
        filePasswords,
        enableDocumentChat: uploadForm.enableDocumentChat,
      }),
    () => {
      setAnswersText("");
      setIntelligentMode(false);
    },
  );

  const validateForm = (): boolean => {
    uploadForm.setPrimaryError(hasForm ? "" : "Upload exactly one PDF or DOCX form template.");
    uploadForm.setSecondaryError(hasAnswers ? "" : "Provide answers as text or upload at least one file.");
    return hasForm && hasAnswers;
  };

  const handleSubmit = (e: React.FormEvent) => uploadForm.handleSubmit(e, validateForm);

  const handleClear = () => {
    setAnswersText("");
    setIntelligentMode(false);
    uploadForm.clearAllFiles();
  };

  return (
    <>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 0.5,
        }}
      >
        Form Filler
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: "var(--t2)", mb: 2, lineHeight: 1.55 }}>
        Upload a <strong style={{ color: "var(--t1)" }}>form</strong> on the left and provide the{" "}
        <strong style={{ color: "var(--t1)" }}>answers or source data</strong> on the right. The system will identify
        fields and fill them automatically.
      </Typography>

      <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <GlassPanel>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--teal)",
                  mb: 1,
                }}
              >
                📄 Form to Complete
              </Typography>
              <Dropzone
                onDrop={uploadForm.createPrimaryDropHandler(isApproved)}
                multiple={false}
                mainText="Upload Form"
                subText="PDF or DOCX form with blanks to fill"
              />
              <UploadedFilesList
                title="Form Template"
                files={uploadForm.primaryFiles}
                onRemoveFile={uploadForm.removePrimaryFile}
                filePasswords={uploadForm.filePasswords}
              />
              {uploadForm.primaryError && (
                <Typography color="error" sx={{ mt: 1, fontSize: "0.78rem" }}>
                  {uploadForm.primaryError}
                </Typography>
              )}
            </GlassPanel>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <GlassPanel>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--teal)",
                  mb: 1,
                }}
              >
                📋 Answers / Source Data
              </Typography>
              <Dropzone
                onDrop={uploadForm.createSecondaryDropHandler(isApproved)}
                multiple
                mainText="Upload Supporting Files (Optional)"
                subText="PDF or DOCX with the information to fill in"
              />
              <UploadedFilesList
                title="Supporting Files"
                files={uploadForm.secondaryFiles}
                onRemoveFile={uploadForm.removeSecondaryFile}
                filePasswords={uploadForm.filePasswords}
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                placeholder={
                  "Type or paste the information to fill into the form…\n\nFor example:\nPolicyholder: John Smith\nPolicy Number: POL-12345\nInsurer: ABC Insurance Ltd"
                }
                value={answersText}
                onChange={(e) => setAnswersText(e.target.value)}
                sx={{ mt: 1 }}
              />
              {uploadForm.secondaryError && (
                <Typography color="error" sx={{ mt: 1, fontSize: "0.78rem" }}>
                  {uploadForm.secondaryError}
                </Typography>
              )}
            </GlassPanel>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <FormControlLabel
            control={
              <Switch checked={intelligentMode} onChange={(e) => setIntelligentMode(e.target.checked)} />
            }
            label="🧠 Intelligent Mode"
            sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
          />
          <Typography sx={{ fontSize: "0.76rem", color: intelligentMode ? "var(--teal)" : "var(--t3)", flex: 1 }}>
            {intelligentMode
              ? "ON — AI may reason and infer from the uploaded content."
              : "OFF — Strict mode. Only explicitly stated facts will be used."}
          </Typography>
        </Box>

        {!canRun && (hasForm || hasAnswers) && (
          <Typography sx={{ fontSize: "0.74rem", color: "var(--warn)", mt: 1 }}>
            Please also add {hasForm ? "answers or source data" : "a form"}.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!isApproved || !canRun || uploadForm.processing}
            sx={{ flex: 3 }}
          >
            ⚡ Fill Form
          </Button>
          <Button variant="outlined" size="large" onClick={handleClear} sx={{ flex: 1 }}>
            ↩ Clear
          </Button>
        </Box>
      </form>
      </Box>
      <InlineProcessingStatus
        open={uploadForm.showLoader}
        files={[...uploadForm.primaryFiles, ...uploadForm.secondaryFiles]}
        jobId={uploadForm.jobId}
        userId={uploadForm.userId}
        onUpload={uploadHandler}
        onComplete={async () => {
          if (uploadForm.jobId) await uploadForm.checkDocumentChatStatus(uploadForm.jobId);
          uploadForm.setProcessing(false);
        }}
        onClose={uploadForm.handleModalClose}
        chatEnabled={uploadForm.documentChatStatus?.chatEnabled}
        hasDocumentChatAccess={uploadForm.hasDocumentChatAccess}
        documentTitle="Form Filler"
      />
      <PdfPasswordModal
        open={uploadForm.showPasswordModal}
        file={uploadForm.currentFileNeedingPassword}
        onSubmit={uploadForm.handlePasswordSubmit}
        onSkip={uploadForm.handlePasswordSkip}
        onCancel={uploadForm.handlePasswordCancel}
      />

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--teal)",
            mb: 1,
          }}
        >
          Recent Forms
        </Typography>
        <GlassPanel>
          <FormFillerList
            refresh={uploadForm.refresh}
            onRefreshComplete={uploadForm.handleRefreshComplete}
          />
        </GlassPanel>
      </Box>
    </>
  );
};

export default FormFillerTab;
